import { Button } from '@/components/ui/button';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ExternalLink, Repeat } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/Breadcrumb';
import { useCart } from '../hooks/useCart';
import { useCollection } from '../hooks/useCollection';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../hooks/useUser';
import AddToCollectionButton from './AddToCollectionButton';
import Card from './Card';
import CardImage from './CardImage';
import CustomizeCardModal from './CustomizeCardModal';
import ManaCost from './ManaCost';
import { BelowTitleAd } from './ads';

export default function CardDetail({ card, showBuyButton = true }) {
  const router = useRouter();
  const { addCardToCart, updateQuantity, getQuantityOfItem } = useCart();
  const { t } = useTranslation();
  const { user, isProMemberActive } = useUserStore();
  const { getQuantityInCollection } = useCollection();
  const cardId = card?.id;
  const quantity = getQuantityOfItem(cardId);
  const collectionQuantity = getQuantityInCollection(cardId);
  const [imageIndex, setImageIndex] = useState(0);
  const [faceIndex, setFaceIndex] = useState(0);
  const [currentView, setCurrentView] = useState('details');
  const [recommendedCards, setRecommendedCards] = useState([]);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [cardVersions, setCardVersions] = useState([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [visibleVersions, setVisibleVersions] = useState(10);
  // Removed addingToCollection state in favor of AddToCollectionButton component

  // Handle dual-faced cards vs regular cards
  const hasTwoFaces = card?.card_faces && card.card_faces.length > 0;
  const currentFace = hasTwoFaces ? card.card_faces[faceIndex] : null;

  // Card data based on whether it's a dual-faced card or not
  const imageSrc = hasTwoFaces ? currentFace?.image_uris?.large : card?.image_uris?.large;
  const cardName = hasTwoFaces ? currentFace?.name : card?.name;
  const setName = card?.set_name;
  const cardType = hasTwoFaces ? currentFace?.type_line : card?.type_line;
  const manaCost = hasTwoFaces ? currentFace?.mana_cost : card?.mana_cost;
  const oracleText = hasTwoFaces ? currentFace?.oracle_text : card?.oracle_text;
  const rarity = card?.rarity;
  const artist = hasTwoFaces ? currentFace?.artist : card?.artist;
  const collectorNumber = card?.collector_number;
  const flavorText = hasTwoFaces ? currentFace?.flavor_text : card?.flavor_text;
  const price = card?.prices?.usd ? `$${card?.prices?.usd}` : '';

  // Flip the card face
  const flipCard = () => {
    setFaceIndex(faceIndex === 0 ? 1 : 0);
  };

  // Collect all available images for this card
  const cardImages = !card?.image_uris && hasTwoFaces ? [currentFace?.image_uris?.large, currentFace?.image_uris?.art_crop].filter(Boolean) : [card?.image_uris?.large, card?.image_uris?.art_crop].filter(Boolean);

  const nextImage = () => {
    setImageIndex((prev) => (prev + 1) % cardImages.length);
  };

  const prevImage = () => {
    setImageIndex((prev) => (prev - 1 + cardImages.length) % cardImages.length);
  };

  useEffect(() => {
    const fetchRecommendedCards = async () => {
      if (!card) return;

      setIsLoadingRecommendations(true);
      try {
        // Fetch random cards as recommendations
        const response = await fetch('/api/scryfall/random');
        if (!response.ok) throw new Error('Failed to fetch recommendations');

        const data = await response.json();
        // Filter out the current card if it happens to be in recommendations
        setRecommendedCards(data.filter((c) => c.id !== cardId));
      } catch (error) {
        console.error('Error fetching recommendations:', error);
      } finally {
        setIsLoadingRecommendations(false);
      }
    };

    fetchRecommendedCards();
  }, [cardId, card]);

  useEffect(() => {
    const fetchCardVersions = async () => {
      if (!card?.name) return;

      setIsLoadingVersions(true);
      try {
        const response = await fetch(`/api/scryfall/search?search=!"${card.name}" unique:prints`);
        if (!response.ok) throw new Error('Failed to fetch card versions');

        const data = await response.json();
        // Filter out the current version and cards without images
        const versions = data.data.filter((version) => version.id !== card.id && version.image_uris?.normal);

        setCardVersions(versions);
      } catch (error) {
        console.error('Error fetching card versions:', error);
        setCardVersions([]);
      } finally {
        setIsLoadingVersions(false);
      }
    };

    fetchCardVersions();
  }, [card?.name, card?.id]);

  const increment = () => {
    if (quantity === 0) {
      addCardToCart({
        ...card,
        image_uris: hasTwoFaces
          ? undefined
          : {
              ...card.image_uris,
              large: card.image_uris?.large,
            },
        card_faces: hasTwoFaces
          ? card.card_faces.map((face) => ({
              ...face,
              image_uris: {
                ...face.image_uris,
                large: face.image_uris?.large,
              },
            }))
          : undefined,
        originalCardId: card.id,
        id: card.id,
      });
    } else {
      updateQuantity(cardId, 1);
    }
  };

  // Removed addCardToCollection function in favor of AddToCollectionButton component

  const formattedUrl = () => {
    const partnerUrl = `https://partner.tcgplayer.com/tcgstacked`;
    const redirectUrl = card?.purchase_uris?.tcgplayer?.split('u=')[1];
    if (!redirectUrl) return null;
    const url = `${partnerUrl}?u=${redirectUrl}`;
    return url;
  };
  const theFormattedUrl = formattedUrl();

  // Schema markup JSON for SEO
  const schemaMarkup = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: cardName,
    image: imageSrc,
    description: oracleText,
    brand: {
      '@type': 'Brand',
      name: 'Magic: The Gathering',
    },
    offers: {
      '@type': 'Offer',
      url: typeof window !== 'undefined' ? window.location.href : '',
      priceCurrency: 'USD',
      price: card?.prices?.usd || '',
      availability: 'https://schema.org/InStock',
    },
  };

  // Get legality information from the card data
  const getLegalityInfo = () => {
    if (!card?.legalities) return [];

    const formats = Object.keys(card.legalities);
    return formats.map((format) => ({
      format,
      status: card.legalities[format],
    }));
  };

  const renderTabContent = () => {
    switch (currentView) {
      case 'details':
        return (
          <>
            <div className='bg-muted/50 rounded-lg p-4 space-y-3'>
              <div className='grid grid-cols-2 gap-x-4 gap-y-2'>
                <div>
                  <span className='text-xs text-muted-foreground block'>{t('card.type')}</span>
                  <span className='font-medium'>{cardType}</span>
                </div>
                <div>
                  <span className='text-xs text-muted-foreground block'>{t('card.rarity')}</span>
                  <span className='font-medium capitalize'>{rarity}</span>
                </div>
                {artist && (
                  <div>
                    <span className='text-xs text-muted-foreground block'>{t('card.artist')}</span>
                    <span className='font-medium'>{artist}</span>
                  </div>
                )}
                {collectorNumber && (
                  <div>
                    <span className='text-xs text-muted-foreground block'>{t('card.collectorNumber')}</span>
                    <span className='font-medium'>{collectorNumber}</span>
                  </div>
                )}
              </div>
            </div>

            {oracleText && (
              <div className='py-4'>
                <h3 className='font-medium text-sm text-muted-foreground mb-2'>{t('card.oracleText')}</h3>
                <p className='whitespace-pre-line'>{oracleText}</p>
              </div>
            )}

            {flavorText && <div className='italic text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg'>&ldquo;{flavorText}&rdquo;</div>}
          </>
        );
      case 'legality':
        const legalities = getLegalityInfo();
        return (
          <div className='space-y-2'>
            {legalities.length > 0 ? (
              <div className='grid grid-cols-2 gap-2'>
                {legalities.map(({ format, status }) => (
                  <div key={format} className='flex items-center'>
                    <div className={`w-2 h-2 rounded-full mr-2 ${status === 'legal' ? 'bg-red-500' : status === 'restricted' ? 'bg-orange-500' : 'bg-red-500'}`}></div>
                    <span className='capitalize font-medium'>{format}:</span>
                    <span className='ml-1 text-sm capitalize text-muted-foreground'>{status === 'legal' ? t('card.legal') : status === 'restricted' ? t('card.restricted') : t('card.banned')}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-muted-foreground py-2'>{t('card.noLegalityInfo')}</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className='min-h-screen bg-gradient-to-b from-background to-background/80 flex-1'>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }} />

        <div className='container mx-auto px-4'>
          <Breadcrumb className='mb-6'>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href='/'>{t('common.home')}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href='/sets'>{t('common.sets')}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/sets/${card?.set}`}>{setName}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{cardName}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full mx-auto bg-card rounded-xl lg:p-6'>
            {/* Left column - Image and gallery controls */}
            <div className='flex flex-col items-center relative'>
              <div className='w-full max-w-md aspect-[745/1040] relative overflow-hidden rounded-lg'>
                <AnimatePresence mode='wait'>
                  <motion.div key={`${faceIndex}-${imageIndex}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className='absolute inset-0'>
                    <CardImage src={cardImages[imageIndex]} alt={cardName} allowZoom={true} className='w-full h-full rounded-lg transition-all duration-300' />
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className='flex items-center justify-center mt-4 space-x-2'>
                {cardImages.length > 1 && (
                  <>
                    <Button variant='outline' size='icon' onClick={prevImage} className='rounded-full'>
                      <ChevronLeft className='w-4 h-4' />
                    </Button>
                    <div className='flex space-x-2'>
                      {cardImages.map((_, idx) => (
                        <button key={idx} className={`w-2 h-2 rounded-full ${imageIndex === idx ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`} onClick={() => setImageIndex(idx)} />
                      ))}
                    </div>
                    <Button variant='outline' size='icon' onClick={nextImage} className='rounded-full'>
                      <ChevronRight className='w-4 h-4' />
                    </Button>
                  </>
                )}

                {hasTwoFaces && card.card_faces.length > 1 && !card.image_uris && (
                  <Button variant='outline' className='ml-4 flex items-center' onClick={flipCard}>
                    <Repeat className='w-4 h-4 mr-2' />
                    Turn Over
                  </Button>
                )}
              </div>
            </div>

            {/* Right column - Card details */}
            <div className='flex flex-col space-y-6'>
              <div>
                <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-2'>
                  <h1 className='text-3xl font-bold text-foreground'>{cardName}</h1>
                  {manaCost && (
                    <div className='flex items-center'>
                      <ManaCost manaCost={manaCost} />
                    </div>
                  )}
                </div>
                <p className='text-sm text-muted-foreground mt-1'>{setName}</p>
              </div>

              <div className='flex space-x-2 overflow-x-auto pb-1'>
                <Button variant={currentView === 'details' ? 'primary' : 'outline'} size='sm' onClick={() => setCurrentView('details')}>
                  {t('card.details')}
                </Button>
                <Button variant={currentView === 'legality' ? 'primary' : 'outline'} size='sm' onClick={() => setCurrentView('legality')}>
                  {t('card.legality')}
                </Button>
              </div>

              <div className='min-h-[250px]'>{renderTabContent()}</div>

              <div className='flex flex-col gap-2 mt-auto pt-4'>
                {quantity > 0 ? (
                  <div className='relative'>
                    <select
                      value={quantity}
                      onChange={(e) => updateQuantity(cardId, parseInt(e.target.value) - quantity)}
                      className='w-full border-0 rounded-md bg-gray-800 text-white hover:bg-gray-900 focus:outline-none text-center text-sm font-medium dark:bg-gray-50 dark:text-gray-900'
                    >
                      <option value={0}>{t('card.removeFromPrintList')}</option>
                      {Array.from({ length: 100 }, (_, i) => i + 1).map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? t('card.cardsInPrintList') : t('card.cardsInPrintListPlural')}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <Button variant='primary' onClick={increment} className='w-full'>
                    {t('card.addToPrintList')}
                  </Button>
                )}
                <div className='flex gap-2 flex-col'>
                  {showBuyButton && process.env.NEXT_PUBLIC_ENABLE_AI === 'true' && <CustomizeCardModal card={card} onCustomize={() => {}} className='flex-1' />}
                  {showBuyButton && theFormattedUrl && (
                    <Button asChild variant='outline' className='flex-1'>
                      <Link href={theFormattedUrl} target='_blank' className='flex items-center justify-center'>
                        <img src='/tcgplayer.webp' alt='TCGplayer' className='w-4 h-4 mr-2' />
                        {price ? `${t('card.buyFor')}${price}` : t('card.buyOn')}
                        <ExternalLink className='w-3 h-3 ml-1 opacity-70' />
                      </Link>
                    </Button>
                  )}
                  {user && <AddToCollectionButton card={card} buttonVariant='outline' className='flex-1' showQuantityInButton={true} />}
                </div>
              </div>
            </div>
          </div>

          {/* Card versions section */}
          {cardVersions.length > 0 && (
            <div className='mb-16 mt-12'>
              <h2 className='text-2xl font-bold mb-6 pb-2'>{t('card.otherVersions')}</h2>
              {isLoadingVersions ? (
                <div className='flex justify-center py-10'>
                  <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-primary'></div>
                </div>
              ) : (
                <div className='space-y-6'>
                  <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6'>
                    {cardVersions.slice(0, visibleVersions).map((version) => (
                      <motion.div key={version.id} transition={{ duration: 0.2 }}>
                        <Card card={version} />
                      </motion.div>
                    ))}
                  </div>
                  {cardVersions.length > visibleVersions && (
                    <div className='flex justify-center'>
                      <Button variant='outline' onClick={() => setVisibleVersions((prev) => prev + 25)} className='w-full max-w-xs'>
                        {t('card.viewMore')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* You might also like section */}
          <div className='mt-16 w-full mx-auto'>
            <h2 className='text-2xl font-bold mb-6 pb-2'>{t('card.youMightAlsoLike')}</h2>

            {isLoadingRecommendations ? (
              <div className='flex justify-center py-10'>
                <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-primary'></div>
              </div>
            ) : recommendedCards.length > 0 ? (
              <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6'>
                {recommendedCards.map((recommendedCard) => (
                  <motion.div key={recommendedCard.id} transition={{ duration: 0.2 }}>
                    <Card card={recommendedCard} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className='text-muted-foreground py-4'>{t('card.noRecommendations')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
